import { useState, useEffect } from "react";
import { applySeo } from "@/lib/seo";
import { BarometerHero } from "@/components/BarometerHero";
import { useAuth } from "@/_core/hooks/useAuth";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { trpc } from "@/lib/trpc";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";
import { generateGeoPdf } from "@/lib/generatePdf";
import { CrossSelling } from "@/components/CrossSelling";

const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const C = {
  bg: "#ffffff",
  bgCard: "#f2f2f2",
  // GEO-Barometer: Navy-Blau
  bordeaux: "#1a3a5c",
  bordeauxDark: "#0f2540",
  bordeauxLight: "#1a3a5c18",
  teal: "#0f2540",
  text: "#000000",
  muted: "#6b7280",
  mutedLight: "#9ca3af",
  border: "#e5e7eb",
};

const BRANCHEN = [
  { value: "", label: "Branche wählen (optional)" },
  { value: "dienstleister", label: "Dienstleister / Beratung" },
  { value: "ecommerce", label: "E-Commerce / Online-Shop" },
  { value: "gastronomie", label: "Gastronomie / Hotel / Tourismus" },
  { value: "gesundheit", label: "Gesundheit / Wellness / Beauty" },
  { value: "b2b", label: "B2B / Software / Technologie" },
  { value: "handwerk", label: "Handwerk / Bau / Immobilien" },
  { value: "kreativ", label: "Kreativbranche / Agentur / Design" },
  { value: "sonstige", label: "Sonstige" },
];

// ─── Typen ────────────────────────────────────────────────────────────────────────────────
interface DimScore { score: number; max: number; details: Record<string, any>; }
interface GEOData {
  url: string; domain: string; page_title: string; total_score: number; max_score: number;
  pages_crawled?: { url: string; type: string; title: string; error: string | null }[];
  scores: Record<string, DimScore>;
  reifegrad: { label: string; emoji: string; desc: string; color: string };
  optimierungen: Array<{ prioritaet: string; dimension: string; titel: string; beschreibung: string; warum: string; schritte: string[]; aufwand: string; zeitaufwand: string; wirkung: string; tools: string[]; }>;
  screenshot_b64: string | null; meta_description: string; error?: string;
}

const DIM_LABELS: Record<string, { label: string; emoji: string }> = {
  strukturierte_daten: { label: "Strukturierte Daten", emoji: "🔖" },
  eeat_signale: { label: "E-E-A-T Signale", emoji: "🎓" },
  content_tiefe: { label: "Content-Tiefe", emoji: "📚" },
  technische_zugaenglichkeit: { label: "Technische Zugänglichkeit", emoji: "⚙️" },
  zitierfahiger_content: { label: "Zitierfähiger Content", emoji: "💬" },
  marken_autoritaet: { label: "Marken-Autorität", emoji: "🏅" },
  frische_aktualitaet: { label: "Frische & Aktualität", emoji: "🗓️" },
  prompt_relevanz: { label: "Prompt-Relevanz", emoji: "❓" },
};

function GeoScoreBar({ score, max, color = "#6d0000" }: { score: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-gray-200 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} /></div>
      <span className="text-xs text-gray-500 w-12 text-right" style={{ fontFamily: "'DM Sans', sans-serif" }}>{score}/{max}</span>
    </div>
  );
}

function GEOResultsView({ data, onReset }: { data: GEOData; onReset: () => void }) {
  const d = data;
  const [activeTab, setActiveTab] = useState<"uebersicht" | "dimensionen" | "optimierungen">("uebersicht");
  const [expandedOpt, setExpandedOpt] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pct = Math.round((d.total_score / d.max_score) * 100);
  const radius = 54; const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (pct / 100) * circumference;

  const handlePdfDownload = () => {
    setPdfLoading(true);
    try { generateGeoPdf(d as unknown as Record<string, unknown>); }
    catch (err) { console.error("[PDF-Fehler GEO]", err); alert("PDF konnte nicht erstellt werden: " + (err instanceof Error ? err.message : String(err))); }
    finally { setTimeout(() => setPdfLoading(false), 1500); }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      <div style={{ height: "4px", backgroundColor: C.bordeaux }}></div>
      <header className="px-6 py-4 flex items-center justify-between sticky top-0 z-50 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3">
          <img src="/manus-storage/logo_2d439bd1.jpg" alt="WERBEAGENTUR Larissa Girenko" className="w-10 h-10 object-contain" />
          <span className="font-black text-sm tracking-widest uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>GEO-Barometer™</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handlePdfDownload} disabled={pdfLoading}
            className="text-white text-xs font-bold uppercase tracking-widest px-4 py-2 disabled:opacity-50"
            style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>
            {pdfLoading ? "⏳ Wird erstellt …" : "📄 PDF"}
          </button>
          <nav className="flex items-center gap-4">
            <a href="/" className="text-xs tracking-widest uppercase text-gray-400 hover:text-black">Sensorik</a>
            <a href="/lokal" className="text-xs tracking-widest uppercase text-gray-400 hover:text-black">Lokal</a>
          </nav>
          <button onClick={onReset} className="text-xs text-gray-500 hover:text-black border border-gray-200 px-4 py-2 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>← Neue Analyse</button>
        </div>
      </header>
      <div className="bg-[#f2f2f2] border-b border-gray-200 px-6 py-3 flex items-center gap-3">
        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
        <span className="text-gray-500 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysiert:</span>
        <span className="text-black text-sm font-medium truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.url}</span>
        {d.page_title && <span className="text-gray-400 text-xs hidden md:block">· {d.page_title}</span>}
      </div>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="bg-[#f2f2f2] border border-gray-200 p-8 flex flex-col items-center justify-center text-center">
            <div className="relative mb-6">
              <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="8" />
                <circle cx="60" cy="60" r={radius} fill="none" stroke={d.reifegrad.color} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-black text-black" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{d.total_score}</span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>von {d.max_score}</span>
              </div>
            </div>
            <div className="text-3xl mb-2">{d.reifegrad.emoji}</div>
            <h2 className="text-xl font-black mb-2" style={{ color: d.reifegrad.color, fontFamily: "'Bebas Neue', sans-serif" }}>{d.reifegrad.label}</h2>
            <p className="text-xs text-gray-500 leading-relaxed max-w-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.reifegrad.desc}</p>
            <div className="mt-4 pt-4 border-t border-gray-200 w-full">
              <p className="text-[10px] text-gray-400 tracking-widest uppercase mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysierte URL</p>
              <p className="text-xs text-gray-600 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.domain}</p>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {d.screenshot_b64 && (
              <div className="border border-gray-200 overflow-hidden">
                <img src={`data:image/jpeg;base64,${d.screenshot_b64}`} alt="Website Screenshot" className="w-full object-cover" style={{ maxHeight: 200 }} />
              </div>
            )}
            <div className="bg-[#f2f2f2] border border-gray-200 p-5">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>KI-Sichtbarkeit in:</p>
              <div className="grid grid-cols-2 gap-3">
                {["ChatGPT", "Perplexity", "Google AI", "Bing Copilot"].map((ki) => {
                  const visible = pct >= 60;
                  return (
                    <div key={ki} className="flex items-center gap-2 p-2 border" style={{ backgroundColor: visible ? '#f0fdf4' : '#fef2f2', borderColor: visible ? '#bbf7d0' : '#fecaca' }}>
                      <span style={{ color: visible ? '#16a34a' : '#dc2626' }}>{visible ? '✓' : '✗'}</span>
                      <span className="text-xs font-medium" style={{ color: visible ? '#15803d' : '#b91c1c', fontFamily: "'DM Sans', sans-serif" }}>{ki}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="flex border-b border-gray-200 mb-8">
          {(["uebersicht", "dimensionen", "optimierungen"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ color: activeTab === tab ? C.bordeaux : C.muted, borderBottom: activeTab === tab ? `2px solid ${C.bordeaux}` : "2px solid transparent", fontFamily: "'DM Sans', sans-serif" }}>
              {tab === "uebersicht" ? "📊 Übersicht" : tab === "dimensionen" ? "🔍 Dimensionen" : "💡 Empfehlungen"}
            </button>
          ))}
        </div>
        {activeTab === "uebersicht" && (
          <div className="space-y-4">
            {Object.entries(d.scores).map(([key, dim]) => {
              const meta = DIM_LABELS[key] || { label: key, emoji: "●" };
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = dp >= 70 ? "#22c55e" : dp >= 40 ? "#eab308" : "#ef4444";
              return (
                <div key={key} className="bg-[#f2f2f2] border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><span className="text-xl">{meta.emoji}</span><span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{meta.label}</span></div>
                    <span className="text-sm font-black" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                  </div>
                  <GeoScoreBar score={dim.score} max={dim.max} color={dc} />
                </div>
              );
            })}
          </div>
        )}
        {activeTab === "dimensionen" && (
          <div className="space-y-4">
            {Object.entries(d.scores).map(([key, dim]) => {
              const meta = DIM_LABELS[key] || { label: key, emoji: "●" };
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = dp >= 70 ? "#22c55e" : dp >= 40 ? "#eab308" : "#ef4444";
              return (
                <div key={key} className="bg-white border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3"><span className="text-xl">{meta.emoji}</span><span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{meta.label}</span></div>
                    <span className="text-sm font-black" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max} ({dp}%)</span>
                  </div>
                  <GeoScoreBar score={dim.score} max={dim.max} color={dc} />
                  <div className="mt-4 space-y-2">
                    {Object.entries(dim.details || {}).map(([dk, dv]: [string, any]) => {
                      const pos = dv?.found === true || (typeof dv?.score === "number" && dv.score > 0);
                      const neg = dv?.found === false || (typeof dv?.score === "number" && dv.score === 0);
                      return (
                        <div key={dk} className="flex items-center gap-2 text-xs">
                          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 font-bold" style={{ color: pos ? "#16a34a" : neg ? "#dc2626" : "#6b7280" }}>{pos ? "✓" : neg ? "✗" : "○"}</span>
                          <span className="text-gray-500 capitalize" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dk.replace(/_/g, " ")}{typeof dv?.score === "number" ? ` (${dv.score}/${dv.max})` : ""}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {activeTab === "optimierungen" && (
          <div className="space-y-3">
            {d.optimierungen.map((opt, i) => (
              <div key={i} className="bg-white border border-gray-200 overflow-hidden"
                style={{ borderLeftWidth: 4, borderLeftColor: opt.prioritaet === "kritisch" ? "#ef4444" : opt.prioritaet === "hoch" ? "#f97316" : opt.prioritaet === "mittel" ? "#eab308" : "#22c55e" }}>
                <button className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50" onClick={() => setExpandedOpt(expandedOpt === i ? null : i)}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-[9px] px-2 py-0.5 border font-bold uppercase tracking-widest flex-shrink-0"
                      style={{ color: opt.prioritaet === "kritisch" ? "#dc2626" : opt.prioritaet === "hoch" ? "#ea580c" : opt.prioritaet === "mittel" ? "#d97706" : "#16a34a", borderColor: opt.prioritaet === "kritisch" ? "#fecaca" : opt.prioritaet === "hoch" ? "#fed7aa" : opt.prioritaet === "mittel" ? "#fde68a" : "#bbf7d0", backgroundColor: opt.prioritaet === "kritisch" ? "#fef2f2" : opt.prioritaet === "hoch" ? "#fff7ed" : opt.prioritaet === "mittel" ? "#fffbeb" : "#f0fdf4" }}>
                      {opt.prioritaet}
                    </span>
                    <span className="text-sm font-bold truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.titel}</span>
                  </div>
                  <span className="text-gray-400 text-xs ml-3">{expandedOpt === i ? "▲" : "▼"}</span>
                </button>
                {expandedOpt === i && (
                  <div className="px-5 pb-6 border-t border-gray-100 bg-[#f2f2f2]">
                    <p className="text-xs text-gray-400 uppercase tracking-widest mt-4 mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.dimension}</p>
                    {opt.warum && (<div className="bg-white border border-gray-200 p-4 mb-4"><p className="text-[10px] uppercase tracking-wider mb-2 font-bold" style={{ color: C.teal, fontFamily: "'DM Sans', sans-serif" }}>💡 Warum wichtig?</p><p className="text-xs text-gray-600 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.warum}</p></div>)}
                    <p className="text-xs text-gray-600 leading-relaxed mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>{opt.beschreibung}</p>
                    {opt.schritte && opt.schritte.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] uppercase tracking-wider mb-3 font-bold" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>📋 Schritt-für-Schritt</p>
                        <div className="space-y-2">
                          {opt.schritte.map((schritt, si) => (
                            <div key={si} className="flex gap-3 text-xs text-gray-600">
                              <span className="flex-shrink-0 w-5 h-5 text-white flex items-center justify-center text-[10px] font-bold" style={{ backgroundColor: C.bordeaux }}>{si + 1}</span>
                              <span className="leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{schritt}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-6 pt-3 border-t border-gray-200">
                      {opt.aufwand && <span className="text-[10px] font-bold text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Aufwand: {opt.aufwand}</span>}
                      {opt.zeitaufwand && <span className="text-[10px] text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>⏱ {opt.zeitaufwand}</span>}
                      {opt.wirkung && <span className="text-[10px] text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Wirkung: <span style={{ color: opt.wirkung === "sehr hoch" ? '#16a34a' : opt.wirkung === "hoch" ? '#d97706' : '#6b7280' }}>{opt.wirkung}</span></span>}
                    </div>
                    {opt.tools && opt.tools.length > 0 && (<div className="mt-3 flex flex-wrap gap-2">{opt.tools.map(tool => (<span key={tool} className="text-[9px] px-2 py-1 border border-gray-200 text-gray-500 bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tool}</span>))}</div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="mt-12 p-8 text-center" style={{ backgroundColor: C.bordeaux }}>
          <p className="text-xs tracking-[0.3em] uppercase text-white/60 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt</p>
          <h3 className="text-2xl font-bold uppercase mb-3 text-white" style={{ fontFamily: "'Roboto', sans-serif" }}>Dein persönlicher GEO-Aktionsplan</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>Der GEO-Barometer zeigt, wo deine Website für KI-Antworten steht. Im Strategie-Gespräch erarbeiten wir gemeinsam, wie du in ChatGPT, Perplexity & Co. sichtbar wirst.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={handlePdfDownload} disabled={pdfLoading} className="text-white font-bold text-sm uppercase tracking-widest px-8 py-3 disabled:opacity-50" style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>{pdfLoading ? "⏳ Wird erstellt …" : "📄 PDF-Report herunterladen"}</button>
            <a href="https://werbeagentur.me" target="_blank" rel="noopener noreferrer" className="bg-white font-bold text-sm uppercase tracking-widest px-8 py-3 hover:bg-gray-100" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>Strategie-Call buchen →</a>
            <button onClick={onReset} className="border border-white/40 text-white font-bold text-sm uppercase tracking-widest px-8 py-3 hover:bg-white/10" style={{ fontFamily: "'DM Sans', sans-serif" }}>Neue Analyse starten</button>
          </div>
        </div>
      </div>
      <footer className="border-t border-gray-200 px-6 py-8 text-center bg-white">
        <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 WERBEAGENTUR Larissa Girenko · GEO-Barometer™ · Dein Schlüssel zum Marketingerfolg</p>
        <p className="text-xs mt-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          <a href="/impressum" className="hover:underline" style={{ color: '#6d0000' }}>Impressum</a>
          <span className="text-gray-300 mx-2">·</span>
          <a href="/datenschutz" className="hover:underline" style={{ color: '#6d0000' }}>Datenschutzerklärung</a>
        </p>
      </footer>
    </div>
  );
}

export default function GEOBarometerHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "GEO-Barometer™ – KI-Sichtbarkeit für dein Unternehmen | WERBEAGENTUR Larissa Girenko",
    description: "Wie oft nennt ChatGPT, Gemini oder Perplexity dein Unternehmen? GEO-Barometer™ misst deine KI-Sichtbarkeit in 8 Dimensionen. Vollanalyse ab 97 €.",
    path: "/geo-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [branche, setBranche] = useState("");
  const [analysisData, setAnalysisData] = useState<GEOData | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const analyze = trpc.geoBarometer.analyze.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        setAnalysisData(res.data as unknown as GEOData);
        setAnalysisError(null);
      } else {
        setAnalysisError((res as { error?: string }).error || "Unbekannter Fehler");
      }
    },
    onError: (err) => { setIsLoading(false); setAnalysisError(err.message); },
  });

  // Preview-Mutation für kostenlose Vorschau (nur Scores)
  const previewMutation = trpc.geoBarometer.analyzePreview.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        setPreviewResult(res.data as Record<string, unknown>);
      } else {
        setAnalysisError((res as Record<string, unknown>).error as string || "Unbekannter Fehler");
      }
    },
    onError: (err) => { setIsLoading(false); setAnalysisError(err.message); },
  });

  const handleAnalyze = () => {
    if (!url.trim()) return;
    setAnalysisError(null);
    setPreviewResult(null);
    setIsLoading(true);
    if (isAdminUser) {
      analyze.mutate({ url: url.trim(), branche });
    } else {
      previewMutation.mutate({ url: url.trim() });
    }
  };

  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "geo_analyse",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/geo-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "geo_mit_videocall",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/geo-barometer",
    });
  };
  const handleReset = () => {
    setAnalysisData(null);
    setAnalysisError(null);
    setPreviewResult(null);
    setIsLoading(false);
    analyze.reset();
    previewMutation.reset();
    setUrl("");
  };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };

  if (analysisData) return <GEOResultsView data={analysisData} onReset={handleReset} />;
  if (isLoading) return <LoadingScreen url={url} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/geo-barometer" accentColor={"#1a3a5c"} />

      <BarometerHero
        name="GEO-Barometer™"
        headline="Empfiehlt ChatGPT<br />dich — oder deine Konkurrenz?"
        subtext={`8 Dimensionen · ChatGPT, Perplexity, Google AI. Kostenlose Vorschau in 60 Sekunden — persönliche Vollanalyse von Larissa ab ${formatPrice(BAROMETER_PRICES.geo)}.`}
        accentColor={C.bordeaux}
        ctaColor="#115751"
        stats={[
          { val: "8", label: "Dimensionen" },
          { val: "100+", label: "Kriterien" },
          { val: "60 Sek.", label: "Vorschau" },
        ]}
        url={url}
        onUrlChange={setUrl}
        onSubmit={handleAnalyze}
        isLoading={isLoading}
        error={analysisError || ""}
        isAdmin={isAdminUser}
        adminCtaText="GEO-Vollanalyse starten →"
      />

      {/* Preview-Ergebnis: nur Scores */}
      {previewResult && (
        <section className="max-w-3xl mx-auto w-full px-6 py-12">
          <div className="border border-gray-200 p-6" style={{ backgroundColor: C.bgCard }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: C.bordeaux }} />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>GEO-Barometer™ Vorschau</p>
              <span className="text-xs text-gray-400 truncate">{url}</span>
            </div>
            {(() => {
              const score = Number(previewResult.total_score ?? 0);
              const maxScore = Number(previewResult.max_score ?? 100);
              const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
              const grade = pct >= 80 ? "A" : pct >= 65 ? "B" : pct >= 45 ? "C" : pct >= 25 ? "D" : "F";
              const gColor = pct >= 70 ? "#16a34a" : pct >= 45 ? "#ca8a04" : "#dc2626";
              return (
                <div className="flex items-center gap-6 mb-6 p-4 bg-white border border-gray-200">
                  <div className="text-center flex-shrink-0">
                    <div className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>{pct}</div>
                    <div className="text-xs text-gray-400">von 100</div>
                  </div>
                  <div>
                    <div className="text-lg font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>Note {grade} · {pct >= 70 ? "KI-sichtbar" : pct >= 45 ? "Ausbaufähig" : "KI-unsichtbar"}</div>
                    <div className="text-sm text-gray-500 mt-1">{score} von {maxScore} Punkten</div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              const dims = (previewResult.dimension_scores as Array<{ label: string; score: number; max: number }>) ?? [];
              return dims.length > 0 ? (
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>Dimensionen auf einen Blick</p>
                  {dims.map((d) => {
                    const dp = d.max > 0 ? Math.round((d.score / d.max) * 100) : 0;
                    const dc = dp >= 70 ? "#16a34a" : dp >= 45 ? "#ca8a04" : "#dc2626";
                    return (
                      <div key={d.label} className="flex items-center gap-3">
                        <span className="text-xs w-40 flex-shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.label}</span>
                        <div className="flex-1 bg-gray-200 h-2"><div style={{ width: `${dp}%`, backgroundColor: dc, height: "100%" }} /></div>
                        <span className="text-xs font-bold w-8 text-right" style={{ color: dc }}>{dp}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.bordeaux }}>{formatPrice(BAROMETER_PRICES.geo)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
              <button onClick={handleReset} className="block w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Andere URL analysieren</button>
            </div>
          </div>
        </section>
      )}


      <section className="py-16 px-6" style={{ backgroundColor: C.bgCard }}>
        {/* KI-Plattformen */}
        <div className="mt-0 max-w-4xl w-full mx-auto">
          <p className="text-xs font-bold tracking-widest uppercase text-center mb-8" style={{ color: C.bordeaux }}>Welche KI-Systeme werden analysiert</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "/manus-storage/7_8bcad2d2.svg", name: "ChatGPT", desc: "OpenAI – meistgenutztes KI-System weltweit" },
              { icon: "/manus-storage/23_1ab7147e.svg", name: "Perplexity", desc: "KI-Suchmaschine mit Quellenangaben" },
              { icon: "/manus-storage/7_8bcad2d2.svg", name: "Google AI", desc: "AI Overviews in den Google-Suchergebnissen" },
              { icon: "/manus-storage/7_8bcad2d2.svg", name: "Gemini", desc: "Google Gemini – KI-Assistent & Search" },
            ].map((p) => (
              <div key={p.name} className="p-5 border text-center" style={{ backgroundColor: C.bgCard, borderColor: C.border }}>
                <img src={p.icon} alt="" className="w-9 h-9 mb-3 object-contain" style={{ opacity: 0.85 }} />
                <div className="font-bold text-sm uppercase tracking-wide mb-1" style={{ color: C.text, fontFamily: "'Roboto', sans-serif" }}>{p.name}</div>
                <div className="text-xs leading-relaxed" style={{ color: C.muted }}>{p.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Dimensionen */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl w-full mx-auto">
          {[
            { icon: "/manus-storage/23_1ab7147e.svg", label: "Strukturierte Daten", sub: "Schema.org, JSON-LD, Markup" },
            { icon: "/manus-storage/23_1ab7147e.svg", label: "E-E-A-T Signale", sub: "Expertise, Autorität, Vertrauen" },
            { icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png", label: "Content-Qualität", sub: "Tiefe, Klarheit, Fakten" },
            { icon: "/manus-storage/32_04885248.svg", label: "Frage-Antwort-Format", sub: "FAQ, Definitionen, How-to" },
            { icon: "/manus-storage/32_04885248.svg", label: "Zitierbarkeit", sub: "Quellenangaben, Studien, Daten" },
            { icon: "/manus-storage/33_d51b7819.svg", label: "Technische Basis", sub: "Speed, Mobile, Core Web Vitals" },
            { icon: "/manus-storage/7_8bcad2d2.svg", label: "Marken-Präsenz", sub: "Social, Erwähnungen, Backlinks" },
            { icon: "/manus-storage/icon_eeat_expertise_60d84526.png", label: "Prompt-Relevanz", sub: "Keyword-Abdeckung, Themenbreite" },
          ].map((f) => (
            <div key={f.label} className="border p-4 text-center" style={{ borderColor: C.border, backgroundColor: C.bgCard }}>
              <img src={f.icon} alt="" className="w-7 h-7 mb-2 object-contain" style={{ opacity: 0.85 }} />
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: C.text, fontFamily: "'Roboto', sans-serif" }}>{f.label}</div>
              <div className="text-xs" style={{ color: C.muted }}>{f.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-20 w-full max-w-5xl mx-auto" style={{ borderTop: `1px solid ${C.border}` }}></div>

        {/* Vorteils-Sektion */}
        <div className="mt-16 max-w-5xl w-full mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.bordeaux }}>Warum der GEO-Barometer?</p>
            <h2 className="font-bold text-3xl md:text-4xl leading-tight" style={{ fontFamily: "'Roboto', sans-serif", color: C.text }}>
              Was du verpasst,<br />
              <span style={{ color: C.bordeaux }}>wenn KI dich nicht kennt</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { number: "01", icon: "/manus-storage/7_8bcad2d2.svg", title: "KI-Systeme werden zur ersten Anlaufstelle", desc: "Bereits 30 % aller Informationssuchen laufen über KI-Assistenten statt über klassische Suchmaschinen. Wer dort nicht vorkommt, verliert täglich potenzielle Kunden – ohne es zu merken." },
              { number: "02", icon: "/manus-storage/33_d51b7819.svg", title: "GEO ist der nächste SEO", desc: "Genau wie SEO in den 2000ern ist GEO heute noch ein Wettbewerbsvorteil für Early Adopters. Wer jetzt optimiert, sichert sich Sichtbarkeit, bevor die Konkurrenz aufwacht." },
              { number: "03", icon: "/manus-storage/icon_content_lesbarkeit_9425589a.png", title: "Konkrete Empfehlungen statt Theorie", desc: "Der GEO-Barometer analysiert 8 Dimensionen und gibt dir priorisierte, sofort umsetzbare Maßnahmen – von Schema.org-Markup bis zur FAQ-Struktur deiner Inhalte." },
              { number: "04", icon: "/manus-storage/23_1ab7147e.svg", title: "Strukturierte Daten als Schlüssel", desc: "KI-Systeme bevorzugen Websites mit klarem Schema.org-Markup. Der Barometer prüft, ob deine Website die richtigen strukturierten Daten enthält, damit KI sie korrekt versteht." },
              { number: "05", icon: "/manus-storage/23_1ab7147e.svg", title: "E-E-A-T: Expertise sichtbar machen", desc: "Expertise, Erfahrung, Autorität und Vertrauen – das sind die Signale, nach denen KI-Systeme suchen. Der Barometer zeigt dir, wie du diese Signale auf deiner Website stärkst." },
              { number: "06", icon: "/manus-storage/7_8bcad2d2.svg", title: "Zukunftssicher aufgestellt sein", desc: "Die KI-Suche wird klassische Suchergebnisse nicht ersetzen, aber ergänzen. Wer beide Kanäle optimiert, hat einen dauerhaften Vorteil gegenüber Mitbewerbern, die nur auf SEO setzen." },
            ].map((v) => (
              <div key={v.number} className="p-6 border" style={{ backgroundColor: C.bgCard, borderColor: C.border }}>
                <div className="flex items-start justify-between mb-4">
                  <img src={v.icon} alt="" className="w-8 h-8 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                  <span className="font-black text-4xl leading-none" style={{ color: `${C.bordeaux}30`, fontFamily: "'Bebas Neue', sans-serif" }}>{v.number}</span>
                </div>
                <h3 className="font-bold text-base mb-3" style={{ fontFamily: "'Roboto', sans-serif", color: C.text }}>{v.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{v.desc}</p>
              </div>
            ))}
          </div>

          {/* Statistik-Banner */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border" style={{ borderColor: C.border }}>
            {[
              { stat: "30 %", label: "aller Suchanfragen laufen bereits über KI-Assistenten", source: "Gartner 2025" },
              { stat: "40 %", label: "weniger organischer Traffic durch AI Overviews erwartet", source: "Gartner 2026" },
              { stat: "60 %", label: "der Websites fehlt grundlegendes Schema.org-Markup", source: "Semrush" },
            ].map((s, i) => (
              <div key={s.stat} className={`p-6 text-center ${i < 2 ? "border-r" : ""}`} style={{ backgroundColor: C.bgCard, borderColor: C.border }}>
                <div className="font-black text-4xl mb-2" style={{ color: C.bordeaux, fontFamily: "'Bebas Neue', sans-serif" }}>{s.stat}</div>
                <div className="text-sm mb-1" style={{ color: C.text }}>{s.label}</div>
                <div className="text-xs" style={{ color: C.muted }}>Quelle: {s.source}</div>
              </div>
            ))}
          </div>

          {/* Zitat */}
          <div className="mt-12 border-l-4 pl-6 py-2 max-w-2xl mx-auto" style={{ borderColor: C.bordeaux }}>
            <p className="text-base italic leading-relaxed" style={{ color: C.muted }}>
              "Generative Engine Optimization ist nicht die Zukunft – es ist die Gegenwart. Wer heute nicht in KI-Antworten erscheint, verliert morgen Marktanteile."
            </p>
            <p className="text-xs font-bold uppercase tracking-widest mt-3" style={{ color: C.bordeaux }}>
              WERBEAGENTUR Larissa Girenko
            </p>
          </div>

          {/* Für wen */}
          <div className="mt-12 p-8 border" style={{ backgroundColor: C.bgCard, borderColor: C.border }}>
            <p className="text-xs font-bold tracking-widest uppercase mb-6 text-center" style={{ color: C.bordeaux }}>Für wen ist der GEO-Barometer?</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: "/manus-storage/47_7e4350f7.svg", label: "KMU & Mittelstand", desc: "Die in KI-Antworten zu ihrer Branche sichtbar werden wollen" },
                { icon: "/manus-storage/21_453003f0.svg", label: "Online-Shops", desc: "Die bei Produktfragen von ChatGPT & Co. empfohlen werden wollen" },
                { icon: "/manus-storage/11_094992a3.svg", label: "Agenturen", desc: "Die ihren Kunden GEO als neuen Service anbieten wollen" },
                { icon: "/manus-storage/47_7e4350f7.svg", label: "Experten & Berater", desc: "Die als Autorität in ihrem Fachgebiet von KI zitiert werden wollen" },
              ].map((t) => (
                <div key={t.label} className="text-center p-4 border bg-white" style={{ borderColor: C.border }}>
                  <img src={t.icon} alt="" className="w-9 h-9 mb-3 object-contain" style={{ opacity: 0.85 }} />
                  <div className="font-bold text-sm uppercase tracking-wide mb-2" style={{ color: C.text, fontFamily: "'Roboto', sans-serif" }}>{t.label}</div>
                  <div className="text-xs leading-relaxed" style={{ color: C.muted }}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Zweiter CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm mb-6 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Kostenlos · 30 Minuten · Persoenliche Analyse von Larissa</p>
            <button onClick={handleBooking}
              className="text-white font-bold text-sm uppercase tracking-widest px-10 py-4 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#115751", fontFamily: "'DM Sans', sans-serif" }}>Jetzt kostenlose Erstberatung buchen</button>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="border-t border-gray-200 py-16 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – GEO-Marketing-Expertin" className="w-full object-cover object-top" style={{ maxHeight: "480px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.bordeaux, fontFamily: "'DM Sans', sans-serif" }}>Gegründet & entwickelt von</p>
              <h2 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>Larissa Girenko</h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Wenn ChatGPT, Perplexity oder Google AI nach Anbietern in deiner Branche gefragt werden – wirst du erwähnt? Die meisten Unternehmen wissen es nicht. Der GEO-Barometer gibt dir die Antwort.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Basierend auf Gartner-Forschung, Semrush-Studien und dem Stand 2025/2026 – das erste messbare System für KI-Sichtbarkeit, das auch kleine Unternehmen ohne Agentur nutzen können.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[{ value: "8", label: "Dimensionen" }, { value: "100+", label: "Kriterien geprüft" }, { value: "Kostenlos", label: "Vorschau" }, { value: formatPrice(BAROMETER_PRICES.geo), label: "Vollversion" }].map((stat) => (
                  <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.bordeaux }}>
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Selling */}
      <CrossSelling
        headline="KI-Sichtbarkeit gesichert – was jetzt?"
        subline="Ergänze das GEO-Barometer mit diesen Analysen für vollständige digitale Sichtbarkeit."
        items={[
          {
            icon: "/manus-storage/1_55991aa3.svg",
            title: "Lokal-Barometer™",
            description: "Google Maps, Bewertungen, lokale Sichtbarkeit – der perfekte Begleiter zum GEO-Barometer.",
            price: formatPrice(BAROMETER_PRICES.lokal),
            href: "/lokal-barometer",
            accent: "#1a3a5c",
            badge: "Ergänzt GEO",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "100+ SEO-Kriterien – klassische Suchmaschinenoptimierung als Basis für KI-Sichtbarkeit.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/11_094992a3.svg",
            title: "Sensorik-Barometer™",
            description: "Wie wirkt deine Website auf das Unterbewusstsein? Sensorische Optimierung für mehr Vertrauen.",
            price: formatPrice(BAROMETER_PRICES.sensorik),
            href: "/sensorik",
            accent: "#6d0000",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: "#f9fafb" }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            <a href="/" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Übersicht</a>
            <a href="/sensorik" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sensorik</a>
            <a href="/lokal" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lokal</a>
            <a href="/seo-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>SEO</a>
            <a href="/conversion-barometer" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Conversion</a>
            <a href="/impressum" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Impressum</a>
            <a href="/datenschutz" className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Datenschutz</a>
          </div>
          <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    "Startseite wird geladen …",
    "Strukturierte Daten werden analysiert …",
    "E-E-A-T Signale werden geprüft …",
    "Content-Qualität wird bewertet …",
    "Frage-Antwort-Formate werden erkannt …",
    "Zitierbarkeit wird analysiert …",
    "Marken-Präsenz wird geprüft …",
    "GEO-Score wird berechnet …",
  ];
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setStepIndex((i) => Math.min(i + 1, steps.length - 1)), 7000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: C.bg }}>
      <div style={{ height: "4px", backgroundColor: C.bordeaux, position: "fixed", top: 0, left: 0, right: 0 }}></div>
      <div className="text-center max-w-lg w-full">
        <div className="relative w-40 h-40 mx-auto mb-10">
          <div className="absolute inset-0 rounded-full border-2" style={{ borderColor: C.border }}></div>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#6d0000] animate-spin" style={{ animationDuration: "1.5s" }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="font-black text-3xl" style={{ color: C.bordeaux, fontFamily: "'Bebas Neue', sans-serif" }}>GEO</div>
              <div className="text-xs tracking-widest uppercase" style={{ color: C.muted }}>Analyse</div>
            </div>
          </div>
        </div>
        <h2 className="font-bold text-2xl uppercase tracking-tight mb-2" style={{ fontFamily: "'Roboto', sans-serif", color: C.text }}>
          GEO-Analyse läuft
        </h2>
        <p className="text-sm mb-8 truncate max-w-xs mx-auto" style={{ color: C.muted }}>{url}</p>
        <div className="border px-6 py-4 mb-8" style={{ backgroundColor: C.bgCard, borderColor: C.border }}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.bordeaux }}></div>
            <span className="text-sm font-medium" style={{ color: C.muted }}>{steps[stepIndex]}</span>
          </div>
        </div>
        <div className="flex justify-center gap-2">
          {steps.map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full transition-colors duration-500" style={{ backgroundColor: i <= stepIndex ? C.bordeaux : C.border }} />
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: C.muted }}>Durchschnittliche Analyse-Zeit: 45–90 Sekunden</p>
      </div>
    </div>
  );
}
